import { format } from "date-fns";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

import { useGetMyAccounts } from "@/api/queries";
import { useUIStateStore } from "@/store/uiStateStore";

import SongDetails from "../features/songs/SongDetails";
import useGetColours from "../helpers/getColours";
import ChooseAccountModal from "./ChooseAccountModal";
import LoadingSmall from "./LoadingSmall";
import RebrandButtonLink from "./RebrandButtonLink";
import { PlusIconMinus, PlusIconPlus } from "./svg/PlusIcon";
import Table from "./Table";

function ExpandCell({ row, isTunecore, highlightColour }) {
  return (
    <>
      {isTunecore ? (
        <span className="float-right">
          {row.isExpanded ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      ) : (
        <span className="float-right">
          {row.isExpanded ? (
            <PlusIconMinus className="h-4" fill={highlightColour} />
          ) : (
            <PlusIconPlus className="h-4" fill={highlightColour} />
          )}
        </span>
      )}
    </>
  );
}

function renderExpandCell(props, isTunecore, highlightColour) {
  return <ExpandCell {...props} isTunecore={isTunecore} highlightColour={highlightColour} />;
}

function TopSongs({ data, isLoading, isError }) {
  const { t } = useTranslation("dashboard");
  const myAccounts = useGetMyAccounts();
  const { highlightColour } = useGetColours();
  const [accountChoiceModal, setAccountChoiceModal] = useState(false);

  const isTunecore = useUIStateStore(state => state.isTunecore);

  const columns = React.useMemo(() => [
    {
      Header: t("dashboard:ra-column-title", "Top Song"),
      accessor: "title",
      width: "25%",
      id: "colTopSongTitle",
    },
    {
      Header: t("dashboard:ra-column-artist-account", "Account"),
      accessor: "accountname",
      width: "25%",
      id: "colTopSongAccount",
    },
    {
      Header: t("dashboard:ra-column-date-added", "Date Added"),
      accessor: "createddate",
      width: "15%",
      id: "colTopSongCreated",
      Cell: ({ row }) =>
        row?.values?.colTopSongCreated
        && row?.values?.colTopSongCreated !== "0000-00-00"
          ? format(Date.parse(row.values.colTopSongCreated), "yyyy-MM-dd")
          : null,
    },
    {
      Header: () => null,
      id: "expander",
      width: "5%",
      Cell: props => renderExpandCell(props, isTunecore, highlightColour),
      SubCell: () => null,
    },
  ].filter(Boolean), [t, isTunecore, highlightColour]);

  return (
    <>
      {!!accountChoiceModal && (
        <ChooseAccountModal
          open={accountChoiceModal}
          title={t("dashboard:choose-account-songs-modal-header", "Choose Account")}
          to="/songs/create"
          setAccountChoiceModal={setAccountChoiceModal}
          accounts={myAccounts?.data?.artistaccounts}
          nonAdminTo="/songs/"
        />
      )}
      <div
        className={`${
          isTunecore
            ? "bg-layer-inverse text-white"
            : "bg-white text-black"
        } w-full p-8 pt-10 rounded-lg`}
      >
        <div className="flex flex-row items-center justify-between mb-5">
          <h2 className="text-xl font-medium ">
            {t("dashboard:recently-added-songs-header", "Recently Added Songs")}
          </h2>
          <RebrandButtonLink
            plusIcon
            id="btnTopSongAddSong"
            onClick={() => {
              setAccountChoiceModal(true);
            }}
          >
            {t("dashboard:add-new-song-link", "Add new song")}
          </RebrandButtonLink>
        </div>
        {isLoading && <LoadingSmall />}
        {!isLoading && !isError && data && (
          <div>
            <Table
              area="TopSongs"
              data={data}
              min={5}
              columns={columns}
              highlightColour={highlightColour}
              revealComponent={<SongDetails />}
              searchSuffix={t("common:songs-search-suffix", "Search Songs")}
              bgClass={`${
                isTunecore ? "bg-layer-inverse" : "bg-white"
              } rounded-lg p-0`}
              initialSort={[
                {
                  id: "createddate",
                  desc: true,
                },
              ]}
            />
          </div>
        )}
        {isError && (
          <div>
            <p>{t("dashboard:recent-songs-error", "Error with finding recent songs")}</p>
          </div>
        )}
      </div>
    </>
  );
}

export default TopSongs;
